import React from 'react';
import Typography from "apollo-react/components/Typography";
import Grid from "apollo-react/components/Grid";
import Box from "apollo-react/components/Box";
import Divider from "apollo-react/components/Divider";
import Paper from "apollo-react/components/Paper";
import Ectd from "./Ectd";
import CommentsList from '../FormApproveComponents/CommentsList/CommentsList';

export const EctdMetadataView = ({ ectdVersion }) => {
  if (!ectdVersion) return null;

  const FieldDisplay = ({ label, value, required = false }) => {
    if (!value && !required) return null;
    return (
      <Box sx={{ marginBottom: 1.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', marginBottom: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="bodyDefault">
          {value || 'N/A'}
        </Typography>
      </Box>
    );
  };

  const CheckboxGroupDisplay = ({ label, items }) => {
    const checkedItems = items.filter(item => item.checked);
    if (checkedItems.length === 0) return null;
    
    return (
      <Box sx={{ marginBottom: 1.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', marginBottom: 0.5 }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {checkedItems.map((item, idx) => (
            <Typography
              key={idx}
              variant="bodySmall"
              sx={{
                padding: '4px 12px',
                backgroundColor: '#e3f2fd',
                borderRadius: '16px',
                display: 'inline-block'
              }}
            >
              {item.label}
            </Typography>
          ))}
        </Box>
      </Box>
    );
  };

  const reviewObjectives = [
    { label: 'Clinical', checked: ectdVersion.clinical },
    { label: 'CMC / Quality', checked: ectdVersion.cmc_quality },
    { label: 'Non-Clinical / Toxicology', checked: ectdVersion.non_clinical_toxicology },
    { label: 'Clinical Pharmacology', checked: ectdVersion.clinical_pharmacology },
    { label: 'Biostatistics', checked: ectdVersion.biostatistics },
    { label: 'Device / Engineering', checked: ectdVersion.device_engineering },
    { label: 'Labeling', checked: ectdVersion.labeling }
  ];

  const expeditedPrograms = [
    { label: 'Fast Track Designation', checked: ectdVersion.fast_track },
    { label: 'Breakthrough Therapy Designation', checked: ectdVersion.breakthrough_therapy },
    { label: 'Regenerative Medicine Advanced Therapy (RMAT)', checked: ectdVersion.rmat },
    { label: 'Accelerated Approval', checked: ectdVersion.accelerated_approval }
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: 2 }}>
        Product Details
      </Typography>
      <Divider sx={{ marginBottom: 2 }} />
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FieldDisplay label="Sequence Number" value={ectdVersion.sequence_number} required={true} />
        </Grid>
        <Grid item xs={12} md={6}>
          <FieldDisplay label="Version Number" value={ectdVersion.version_number} required={true} />
        </Grid>
        <Grid item xs={12} md={6}>
          <FieldDisplay label="Product Name" value={ectdVersion.product_name} />
        </Grid>
        <Grid item xs={12} md={6}>
          <FieldDisplay label="Submission Type" value={ectdVersion.submission_type} />
        </Grid>
        <Grid item xs={12} md={6}>
          <FieldDisplay label="Biologic Product Type" value={ectdVersion.biologic_product_type} />
        </Grid>
        <Grid item xs={12} md={6}>
          <FieldDisplay label="Basis of Clinical Efficacy" value={ectdVersion.basis_of_clinical_efficacy} />
        </Grid>
        <Grid item xs={12} md={6}>
          <FieldDisplay label="Dosage Form & Route of Administration" value={ectdVersion.dosage_form_route} />
        </Grid>
        
        {ectdVersion.proposed_indication && ectdVersion.proposed_indication.length > 0 && (
          <Grid item xs={12}>
            <Box sx={{ marginBottom: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', marginBottom: 0.5 }}>
                Proposed Indication
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {ectdVersion.proposed_indication
                  .filter(indication => indication)
                  .map((indication, idx) => (
                    <Typography
                      key={idx}
                      variant="bodySmall"
                      sx={{
                        padding: '4px 12px',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '16px',
                        display: 'inline-block'
                      }}
                    >
                      {indication}
                    </Typography>
                  ))}
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

const EctdClarificationWave = ({ 
  ectdVersion, 
  treeData, 
  requestProgressId, 
  requestProgressStatus, 
  comments, 
  setComments, 
  requestId,
  ectdFileId,
  ectdFile
}) => {
  return (
    <Paper sx={{ padding: 3, width: '100%' }}>
      {ectdVersion ? (
        <EctdMetadataView ectdVersion={ectdVersion} />
      ) : (
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: 2 }}>
            Product Details
          </Typography>
          <Divider sx={{ marginBottom: 2 }} />
          <Box sx={{ 
            padding: 3, 
            backgroundColor: '#f9f9f9', 
            borderRadius: 1,
            textAlign: 'center'
          }}>
            <Typography variant="body1" color="textSecondary">
              No eCTD version information available for this clarification wave
            </Typography>
          </Box>
        </Box>
      )}
      
      <Box sx={{ marginTop: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: 2 }}>
          eCTD Document
        </Typography>
        <Divider sx={{ marginBottom: 2 }} />
        
        {ectdFileId ? (
          <Ectd
            treeData={treeData}
            ectdFileId={ectdFileId}
            role="customer"
            requestProgressId={requestProgressId}
            requestProgressStatus={requestProgressStatus}
            comments={comments}
            setComments={setComments}
            requestId={requestId}
            ectdVersion={ectdVersion}
            uploadedFile={ectdFile}
            hideMetadata={true}
            skipCommentsFetch={true}
          />
        ) : (
          <Box sx={{ 
            padding: 3, 
            backgroundColor: '#f9f9f9', 
            borderRadius: 1,
            textAlign: 'center'
          }}>
            <Typography variant="body1" color="textSecondary">
              No eCTD document available for this clarification wave
            </Typography>
          </Box>
        )}
      </Box>

      {comments && comments.length > 0 && (
        <Box 
          sx={{ 
            marginTop: 4,
            isolation: 'isolate',
            position: 'relative',
            zIndex: 1
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: 2 }}>
            Comments
          </Typography>
          <Divider sx={{ marginBottom: 2 }} />
          
          <CommentsList
            prevComments={comments}
            pagination={{}}
            filters={{}}
            updateFilters={() => {}}
            showControls={false}
            showFilters={false}
            showSorting={false}
            showPageSize={false}
            showResultsInfo={false}
            showPagination={false}
            showCommentsTitle={false}
          />
        </Box>
      )}
    </Paper>
  );
};

export default EctdClarificationWave;

